import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { localizeApiError } from "@/lib/apiErrors";
import { useI18n } from "@/context/I18nContext";
import { CATEGORIES, categoryColor } from "@/lib/categories";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, CreditCard, Trash2, Pencil, BellRing } from "lucide-react";

const CYCLES = ["monthly", "yearly", "weekly", "quarterly"];
const CURRENCIES = ["PLN", "EUR", "USD", "GBP", "JPY", "INR"];

const empty = { name: "", amount: "", currency: "PLN", billing_cycle: "monthly", next_renewal: "", category: "Software", notes: "" };

function urgencyStyle(u) {
  const map = {
    overdue: { bg: "var(--danger-bg)", fg: "var(--danger-fg)", bd: "var(--danger-border)" },
    warning: { bg: "var(--warning-bg)", fg: "var(--warning-fg)", bd: "var(--warning-border)" },
    info: { bg: "var(--info-bg)", fg: "var(--info-fg)", bd: "var(--info-border)" },
    ok: { bg: "rgba(255,255,255,0.05)", fg: "#9aa4b2", bd: "rgba(255,255,255,0.1)" },
    unknown: { bg: "rgba(255,255,255,0.05)", fg: "#9aa4b2", bd: "rgba(255,255,255,0.1)" },
  };
  return map[u] || map.ok;
}

export default function Subscriptions() {
  const ctx = useOutletContext();
  const { t, tc, tcycle } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const renewalText = (s) => {
    if (s.days_until_renewal == null) return t("renewal.noDate");
    if (s.days_until_renewal < 0) return t("renewal.overdue", { n: Math.abs(s.days_until_renewal) });
    if (s.days_until_renewal === 0) return t("renewal.renewsToday");
    return t("renewal.renewsIn", { n: s.days_until_renewal });
  };

  const load = () => {
    setLoading(true);
    api.listSubscriptions().then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, amount: s.amount, currency: s.currency, billing_cycle: s.billing_cycle, next_renewal: s.next_renewal || "", category: s.category, notes: s.notes || "" });
    setEditId(s.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("toast.nameRequired")); return; }
    setSaving(true);
    const payload = { ...form, amount: Number(form.amount) || 0 };
    try {
      if (editId) { await api.updateSubscription(editId, payload); toast.success(t("toast.subUpdated")); }
      else { await api.createSubscription(payload); toast.success(t("toast.subAdded")); }
      setOpen(false); load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = localizeApiError(e, t, "toast.saveError");
      toast.error(detail);
      if (status === 402) { setOpen(false); ctx?.openUpgrade?.(); }
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.deleteSubscription(id); setItems((x) => x.filter((s) => s.id !== id)); toast.success(t("toast.subDeleted")); }
    catch { toast.error(t("toast.deleteError")); }
  };

  const monthlyTotal = items.reduce((sum, s) => {
    const a = Number(s.amount || 0);
    const f = { monthly: a, yearly: a / 12, weekly: a * 52 / 12, quarterly: a / 3 }[s.billing_cycle] || a;
    return sum + f;
  }, 0);

  return (
    <div data-testid="subscriptions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{t("subs.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("subs.summary", { count: items.length, total: formatMoney(monthlyTotal) })}</p>
        </div>
        <Button data-testid="add-subscription-button" onClick={openNew} className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
          <Plus style={{ width: 18, height: 18 }} className="mr-1.5" /> {t("subs.add")}
        </Button>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0,1,2].map((i) => <Skeleton key={i} className="h-40 bg-white/5" />)}</div>
      ) : items.length === 0 ? (
        <div className="card-base p-12 text-center">
          <CreditCard className="text-muted-foreground mx-auto mb-3" style={{ width: 28, height: 28 }} />
          <p className="font-display font-semibold">{t("subs.noneTitle")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("subs.noneDesc")}</p>
          <Button onClick={openNew} className="mt-4 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">{t("subs.add")}</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => {
            const u = urgencyStyle(s.urgency);
            return (
              <div key={s.id} data-testid="subscription-card" className="card-base p-5 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${categoryColor(s.category)}1a`, border: `1px solid ${categoryColor(s.category)}40` }}>
                      <CreditCard style={{ width: 18, height: 18, color: categoryColor(s.category) }} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{tc(s.category)}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid="edit-subscription-button" onClick={() => openEdit(s)} aria-label={t("common.update")} className="text-muted-foreground hover:text-cyan-300 p-1.5"><Pencil style={{ width: 15, height: 15 }} /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid="delete-subscription-button" aria-label={t("common.delete")} className="text-muted-foreground hover:text-rose-300 p-1.5"><Trash2 style={{ width: 15, height: 15 }} /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-surface-1 border-white/10">
                        <AlertDialogHeader><AlertDialogTitle>{t("subs.deleteTitle")}</AlertDialogTitle><AlertDialogDescription>{t("subs.deleteDesc", { name: s.name })}</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-white/5 border-white/10">{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction data-testid="confirm-delete-subscription" onClick={() => del(s.id)} className="bg-rose-500 hover:bg-rose-400 text-white">{t("common.delete")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-display font-semibold tabular-nums">{formatMoney(s.amount, s.currency)}</p>
                    <p className="text-xs text-muted-foreground">{tcycle(s.billing_cycle)}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1" style={{ background: u.bg, color: u.fg, border: `1px solid ${u.bd}` }}>
                    <BellRing style={{ width: 12, height: 12 }} /> {renewalText(s)}
                  </span>
                </div>
                {s.next_renewal && <p className="text-xs text-muted-foreground mt-2">{t("renewal.next", { date: formatDate(s.next_renewal) })}</p>}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface-1 border-white/10" data-testid="subscription-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("subs.editTitle") : t("subs.addTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">{t("field.name")}</Label>
              <Input data-testid="subscription-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("subs.namePlaceholder")} className="mt-1 bg-white/5 border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">{t("field.amount")}</Label>
                <Input data-testid="subscription-amount-input" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-white/5 border-white/10 font-mono" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("field.currency")}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">{t("field.billingCycle")}</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger data-testid="subscription-cycle-select" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>{CYCLES.map((c) => <SelectItem key={c} value={c}>{tcycle(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("field.category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: categoryColor(c) }} />{tc(c)}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("field.nextRenewal")}</Label>
              <Input data-testid="subscription-renewal-date-input" type="date" value={form.next_renewal} onChange={(e) => setForm({ ...form, next_renewal: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/15 bg-white/5">{t("common.cancel")}</Button>
            <Button data-testid="save-subscription-button" onClick={save} disabled={saving} className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">{saving ? t("common.saving") : editId ? t("common.update") : t("common.add")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
